"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfigurationPushPullForceUtil = void 0;
const echain_math_1 = require("@igus/echain-math");
const echain_shared_articles_oo_1 = require("@igus/echain-shared-articles-oo");
const echain_shared_articles_1 = require("@igus/echain-shared-articles");
const echain_shared_quantities_1 = require("@igus/echain-shared-quantities");
const echain_shared_logging_1 = require("@igus/echain-shared-logging");
const echain_shared_various_1 = require("@igus/echain-shared-various");
/**
 * A Util class to bundle functions to calculate the PPF
 */
class ConfigurationPushPullForceUtil {
    static isECPPushPullForceValid(config, ppfConfig = undefined) {
        var _a, _b;
        const ecpPPF = ConfigurationPushPullForceUtil.calculateECPPushPullForce(config, ppfConfig);
        const ppfMax = (_b = (_a = config.kette) === null || _a === void 0 ? void 0 : _a.pushPullForceMax) === null || _b === void 0 ? void 0 : _b.N;
        return ecpPPF !== undefined && ppfMax !== undefined && ecpPPF < ppfMax;
    }
    static calculateECPPushPullForce(config, ppfConfig = undefined) {
        var _a, _b, _c, _d, _e;
        const ecpConfig = (_a = config.getSmartPlasticsConfiguration()) === null || _a === void 0 ? void 0 : _a.ECP;
        const weightPerLength = (_c = (_b = ppfConfig === null || ppfConfig === void 0 ? void 0 : ppfConfig.weightPerLength) === null || _b === void 0 ? void 0 : _b.toJson()) !== null && _c !== void 0 ? _c : ecpConfig === null || ecpConfig === void 0 ? void 0 : ecpConfig.iSenseFillWeight;
        if (!(ecpConfig === null || ecpConfig === void 0 ? void 0 : ecpConfig.iSenseModule) || !weightPerLength || !config.kette) {
            return;
        }
        const ketteJson = config.kette.toJson();
        ppfConfig !== null && ppfConfig !== void 0 ? ppfConfig : (ppfConfig = {
            beschleunigungMax: config.beschleunigungMax,
            einbauart: echain_shared_articles_1.Einbauart.gleitend,
            verfahrweg: (_d = config.kette) === null || _d === void 0 ? void 0 : _d.verfahrweg,
            versatz: (_e = config.kette) === null || _e === void 0 ? void 0 : _e.versatz,
            weightPerLength: echain_shared_quantities_1.Q.optional(weightPerLength),
        });
        const frictionFactor = echain_shared_articles_oo_1.ChainFriction.getFrictionFactor(ketteJson, echain_shared_articles_1.Einbauart.gleitend);
        return ConfigurationPushPullForceUtil.calculatePushPullForce(ketteJson, ppfConfig, frictionFactor);
    }
    /**
     * get the push pull configuration Args of given configuration
     */
    static getPushPullConfigurationArgs(configuration) {
        var _a, _b;
        const zusatzlast = configuration.getZusatzlastClone();
        const beschleunigungMax = configuration.getBeschleunigungMaxClone();
        const einbauart = configuration.getEinbauart();
        const verfahrweg = echain_shared_quantities_1.Q.optional((_a = configuration.ketteFilterProperties) === null || _a === void 0 ? void 0 : _a.verfahrweg_mm, 'mm');
        const versatz = (0, echain_shared_quantities_1.Q)(((_b = configuration.ketteFilterProperties) === null || _b === void 0 ? void 0 : _b.versatz_mm) || 0, 'mm');
        const weightPerLength = configuration.getLeitungenCount() > 0 ? configuration.getLeitungenClone().getTotalWeightPerLength() : zusatzlast;
        return { beschleunigungMax, einbauart, verfahrweg, versatz, weightPerLength };
    }
    /**
     * calculate the push pull force by given kette and PushPullForceCalculationArgs.
     */
    static calculatePushPullForce(kette, pushPullForceCalculationArgs, frictionFactor) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        (_a = kette.K2_mm) !== null && _a !== void 0 ? _a : (kette.K2_mm = (_b = echain_shared_quantities_1.Q.optional(kette.K2)) === null || _b === void 0 ? void 0 : _b.mm);
        (_c = kette.GewichtKgProM_kg_m) !== null && _c !== void 0 ? _c : (kette.GewichtKgProM_kg_m = (_d = echain_shared_quantities_1.Q.optional(kette.GewichtKgProM)) === null || _d === void 0 ? void 0 : _d.kg_m);
        (_e = kette.Teilung_mm) !== null && _e !== void 0 ? _e : (kette.Teilung_mm = (_f = echain_shared_quantities_1.Q.optional(kette.Teilung)) === null || _f === void 0 ? void 0 : _f.mm);
        (_g = kette.K_mm) !== null && _g !== void 0 ? _g : (kette.K_mm = (_h = echain_shared_quantities_1.Q.optional(kette.K)) === null || _h === void 0 ? void 0 : _h.mm);
        return kette.KetteSerieType === echain_shared_articles_1.KetteSerieType.rolEchain
            ? this.calculateRolEchainPushPullForce(kette, pushPullForceCalculationArgs, frictionFactor)
            : this.calculateDefaultPushPullForce(kette, pushPullForceCalculationArgs, frictionFactor);
    }
    static calculateRolEchainPushPullForce(kette, { beschleunigungMax, einbauart, verfahrweg, versatz = echain_shared_quantities_1.Q.Length.m(0), weightPerLength, }, frictionFactor) {
        // invalid PushPullForceCalculationArgs?
        if (!weightPerLength || !verfahrweg || !einbauart || !beschleunigungMax || einbauart !== echain_shared_articles_1.Einbauart.gleitend) {
            return undefined;
        }
        // invalid kette
        if (!kette.GewichtKgProM_kg_m || !kette.ApproxVerfahrwegToEffektiveLaenge) {
            return undefined;
        }
        const formula = echain_shared_various_1.ApproxFormulaUtil.createLinearFormula({
            samplePoints: kette.ApproxVerfahrwegToEffektiveLaenge,
            targetX: verfahrweg.m,
        });
        if (!formula) {
            return undefined;
        }
        const approxEffektiveLaengeWithoutVersatz = echain_shared_quantities_1.Q.Length.m(formula.m * verfahrweg.m + formula.b);
        const approxEffektiveLaenge = echain_shared_quantities_1.Q.Length.m(approxEffektiveLaengeWithoutVersatz.m + versatz.m);
        const approxEffektiveLaengeWithSafety = echain_shared_quantities_1.Q.Length.m(approxEffektiveLaenge.m * 1.03); // 3% tolerance
        const calculatedPushPullForce_gleitend = echain_shared_quantities_1.Force.N(echain_math_1.KetteMath.calcMaxPushPullForceGleitend_N(weightPerLength.kg_m, kette.GewichtKgProM_kg_m, approxEffektiveLaengeWithSafety.mm, beschleunigungMax.m_s2, frictionFactor));
        return calculatedPushPullForce_gleitend.N;
    }
    static calculateDefaultPushPullForce(ketteArgs, { beschleunigungMax, einbauart, verfahrweg, versatz, weightPerLength }, frictionFactor) {
        // invalid PushPullForceCalculationArgs?
        if (!weightPerLength || !verfahrweg || !einbauart || !beschleunigungMax || einbauart === echain_shared_articles_1.Einbauart.freitragend) {
            return undefined;
        }
        // invalid ketteArgs?
        if (!ketteArgs.GewichtKgProM_kg_m || !ketteArgs.Teilung_mm) {
            return undefined;
        }
        if (einbauart === echain_shared_articles_1.Einbauart.gleitend) {
            if (!ketteArgs.K2_mm) {
                return undefined;
            }
            const effektiveLaenge_mm_gleitend = echain_math_1.KetteMath.calcEffektiveLaengeFromVerfahrwegWithLangerWeg_mm(verfahrweg.mm, ketteArgs.K2_mm, versatz.mm, ketteArgs.Teilung_mm);
            const calculatedPushPullForce_gleitend = echain_shared_quantities_1.Force.N(echain_math_1.KetteMath.calcMaxPushPullForceGleitend_N(weightPerLength.kg_m, ketteArgs.GewichtKgProM_kg_m, effektiveLaenge_mm_gleitend, beschleunigungMax.m_s2, frictionFactor));
            return calculatedPushPullForce_gleitend.N;
        }
        // .. haengend
        if (!ketteArgs.K_mm) {
            return undefined;
        }
        const effektiveLaenge_mm_haengend = echain_math_1.KetteMath.calcEffektiveLaengeFromVerfahrweg_mm(verfahrweg.mm, ketteArgs.K_mm, versatz.mm, ketteArgs.Teilung_mm);
        const calculatedPushPullForce_haengend = echain_shared_quantities_1.Force.N(echain_math_1.KetteMath.calcMaxPushPullForceHaengend_N(weightPerLength.kg_m, ketteArgs.GewichtKgProM_kg_m, effektiveLaenge_mm_haengend, beschleunigungMax.m_s2, frictionFactor));
        return calculatedPushPullForce_haengend.N;
    }
    /**
     * In dependence of Push Pull Force check if the application requires a "Stuetzblech"
     * FPPF <= 0,5 * Fmax. => Kein Stützblech
     * FPPF > 0,5 * Fmax  => Stützblech
     */
    static applicationRequiresStuetzblech(configuration, kette) {
        const einbauart = configuration.getEinbauart();
        if (einbauart !== echain_shared_articles_1.Einbauart.gleitend)
            return undefined;
        const pushPullForceCalculationArgs = ConfigurationPushPullForceUtil.getPushPullConfigurationArgs(configuration);
        let K2_mm;
        let GewichtKgProM_kg_m;
        let Teilung_mm;
        let PushPullForceMax_N;
        let K_mm;
        if (kette instanceof echain_shared_articles_oo_1.Kette) {
            if (kette.k2 && kette.gewichtKgProM && kette.teilung && kette.pushPullForceMax && kette.k) {
                K2_mm = kette.k2.mm;
                GewichtKgProM_kg_m = kette.gewichtKgProM.kg_m;
                Teilung_mm = kette.teilung.mm;
                PushPullForceMax_N = kette.pushPullForceMax.N;
                K_mm = kette.k.mm;
            }
            else {
                return false;
            }
        }
        else {
            K2_mm = kette.K2_mm;
            GewichtKgProM_kg_m = kette.GewichtKgProM_kg_m;
            Teilung_mm = kette.Teilung_mm;
            PushPullForceMax_N = kette.PushPullForceMax_N;
            K_mm = kette.K_mm;
        }
        const ketteArgs = { K2_mm, GewichtKgProM_kg_m, Teilung_mm, PushPullForceMax_N, K_mm };
        const pushPullforce_N = ConfigurationPushPullForceUtil.calculatePushPullForce(ketteArgs, pushPullForceCalculationArgs, echain_shared_articles_oo_1.ChainFriction.getFrictionFactor(kette, einbauart));
        if (pushPullforce_N && PushPullForceMax_N) {
            return pushPullforce_N > 0.5 * PushPullForceMax_N;
        }
        throw new echain_shared_logging_1.SentryError(`${this.className}.applicationRequiresStuetzblech(): pushPullforce_N ${pushPullforce_N}`);
    }
}
exports.ConfigurationPushPullForceUtil = ConfigurationPushPullForceUtil;
ConfigurationPushPullForceUtil.className = 'ConfigurationPushPullForceUtil';
//# sourceMappingURL=ConfigurationPushPullForceUtil.js.map